/**
 * Base API Service
 *
 * @package Enterprise\BookingSystem\Services
 */

/**
 * WordPress dependencies
 */
import apiFetch from "@wordpress/api-fetch";

/**
 * Internal dependencies
 */
import { ApiResponse, ApiError, RequestConfig } from "./types";

export abstract class BaseApiService {
	protected readonly baseUrl: string;
	private readonly SESSION_KEY = "ebs_session_id";

	constructor(baseUrl = "/ebs/v1") {
		this.baseUrl = baseUrl;
	}

	/**
	 * Resolve the wp_rest nonce from whichever localized data object is present
	 * on the current page (storefront data stores, checkout block, or admin app).
	 * WordPress core uses this to authenticate cookie-based REST requests and to
	 * protect mutation endpoints against CSRF.
	 */
	private getNonce(): null | string {
		return (
			( globalThis as any ).ebsConfig?.nonce ??
			( globalThis as any ).ebsCheckoutData?.nonce ??
			( globalThis as any ).ebsAdmin?.nonce ??
			null
		);
	}

	/**
	 * Get session ID from localStorage
	 */
	private getSessionId(): null | string {
		try {
			return localStorage.getItem(this.SESSION_KEY);
		} catch {
			return null;
		}
	}

	/**
	 * Set session ID in localStorage
	 */
	private setSessionId(sessionId: string): void {
		try {
			localStorage.setItem(this.SESSION_KEY, sessionId);
		} catch {
			// Silently fail if localStorage is not available
		}
	}

	/**
	 * Make an API request
	 */
	protected async request<T>(
		endpoint: string,
		config: RequestConfig = {},
	): Promise<T> {
		const { method = "GET", data, headers = {}, parse = true } = config;

		// Add session ID header if available (fallback for when cookies don't work)
		const sessionId = this.getSessionId();
		const nonce     = this.getNonce();
		const requestHeaders = {
			...headers,
			...(sessionId ? { "X-EBS-Session-ID": sessionId } : {}),
			// X-WP-Nonce lets WordPress core verify the wp_rest nonce for
			// cookie-authenticated requests, protecting against CSRF.
			...(nonce ? { "X-WP-Nonce": nonce } : {}),
		};

		try {
			const response = await apiFetch<ApiResponse<T> | T>({
				path: `${this.baseUrl}${endpoint}`,
				method,
				data,
				headers: requestHeaders,
				parse,
			});

			// Handle wrapped API responses
			if (this.isApiResponse(response)) {
				if (!response.success) {
					throw new ApiError(
						response.message,
						undefined,
						undefined,
						response,
					);
				}

				// Store session ID if present in response
				if (
					response.data &&
					typeof response.data === "object" &&
					"sessionId" in response.data
				) {
					const respData = response.data as Record<string, unknown>;
					if (typeof respData.sessionId === "string") {
						this.setSessionId(respData.sessionId);
					}
				}

				return response.data;
			}

			// Handle direct responses
			return response as T;
		} catch (error) {
			throw this.handleError(error);
		}
	}

	/**
	 * Type guard for ApiResponse
	 */
	private isApiResponse<T>(response: unknown): response is ApiResponse<T> {
		return (
			typeof response === "object" &&
			response !== null &&
			"success" in response &&
			"message" in response &&
			"data" in response
		);
	}

	/**
	 * Handle and transform errors
	 */
	protected handleError(error: unknown): ApiError {
		// Already an ApiError
		if (error instanceof ApiError) {
			return error;
		}

		// Standard Error
		if (error instanceof Error) {
			return new ApiError(error.message);
		}

		// Object with message property
		if (
			typeof error === "object" &&
			error !== null &&
			"message" in error &&
			typeof error.message === "string"
		) {
			const statusCode =
				"code" in error && typeof error.code === "number"
					? error.code
					: undefined;
			return new ApiError(error.message, undefined, statusCode, error);
		}

		// Unknown error
		return new ApiError(
			"An unknown error occurred",
			undefined,
			undefined,
			error,
		);
	}

	/**
	 * GET request
	 */
	protected async get<T>(endpoint: string): Promise<T> {
		return this.request<T>(endpoint, { method: "GET" });
	}

	/**
	 * POST request
	 */
	protected async post<T>(endpoint: string, data: object): Promise<T> {
		return this.request<T>(endpoint, {
			method: "POST",
			data: data as Record<string, unknown>,
		});
	}

	/**
	 * PUT request
	 */
	protected async put<T>(endpoint: string, data: object): Promise<T> {
		return this.request<T>(endpoint, {
			method: "PUT",
			data: data as Record<string, unknown>,
		});
	}

	/**
	 * DELETE request
	 */
	protected async delete<T>(endpoint: string): Promise<T> {
		return this.request<T>(endpoint, { method: "DELETE" });
	}
}
